<template>
  <div class="onboarding-widget">
    <!-- Componente de celebracao (sempre renderizado para funcionar mesmo apos completar) -->
    <onboarding-celebration v-model="showCelebration" />

    <!-- Conteudo do widget (escondido quando onboarding completo) -->
    <template v-if="shouldShowWidget">
      <!-- FAB quando minimizado (escondido quando há dialog fullscreen) -->
      <transition name="fab-fade">
        <v-btn v-if="!isExpanded && !hasFullscreenDialog"
               fab
               fixed
               bottom
               right
               color="primary"
               class="onboarding-fab"
               :style="{ bottom: '24px', right: '24px' }"
               @click="toggleExpanded">
          <v-progress-circular :value="totalProgress"
                               :size="48"
                               :width="4"
                               color="white"
                               class="progress-circle">
            <span class="progress-text white--text">{{ totalProgress }}%</span>
          </v-progress-circular>
        </v-btn>
      </transition>

      <!-- Sidebar quando expandido (escondido quando há dialog fullscreen) -->
      <v-navigation-drawer v-if="!hasFullscreenDialog"
                           v-model="isExpanded"
                           right
                           app
                           hide-overlay
                           stateless
                           :width="$vuetify.breakpoint.smAndDown ? '100%' : 400"
                           class="onboarding-sidebar">
        <!-- Header -->
        <div class="sidebar-header pa-4">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-rocket-launch</v-icon>
              <span class="header-title">Primeiros Passos</span>
            </div>
            <v-btn icon small @click="toggleExpanded">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <p class="text-caption grey--text mt-2 mb-0">
            Complete as etapas abaixo para configurar sua plataforma
          </p>
        </div>

        <!-- Barra de progresso geral -->
        <div class="progress-section pa-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-caption font-weight-medium">Progresso geral</span>
            <span class="text-caption primary--text font-weight-bold">{{ totalProgress }}%</span>
          </div>
          <v-progress-linear :value="totalProgress"
                             color="primary"
                             background-color="grey lighten-3"
                             height="8"
                             rounded/>
          <div class="text-caption grey--text mt-1">
            {{ completedStepsCount }} de {{ totalStepsCount }} etapas concluidas
          </div>
        </div>

        <v-divider />

        <!-- Lista de grupos -->
        <div class="groups-container pa-4">
          <v-expansion-panels v-model="expandedPanel"
                              accordion
                              flat>
            <onboarding-group v-for="(categoryData, category) in orderedCategories"
                              :key="category"
                              :category="category"
                              :title="getCategoryTitle(category)"
                              :steps="categoryData.steps"/>
          </v-expansion-panels>
        </div>

        <!-- Footer com dica -->
        <div class="sidebar-footer pa-4">
          <v-alert dense
                   text
                   color="info"
                   class="mb-0">
            <template #prepend>
              <v-icon small color="info" class="mr-3">mdi-lightbulb-outline</v-icon>
            </template>
            <span class="text-caption">
              Complete todas as etapas para desbloquear todo o potencial da plataforma!
            </span>
          </v-alert>
        </div>
      </v-navigation-drawer>
    </template>
  </div>
</template>

<script lang="ts">
import Vue, { defineComponent, ref, computed, onMounted, onUnmounted, watch, getCurrentInstance, nextTick } from 'vue'
import OnboardingGroup from './OnboardingGroup.vue'
import OnboardingCelebration from './OnboardingCelebration.vue'

// Mapeamento de categoria para titulo
const categoryTitles: Record<string, string> = {
  setup: 'Configuracao Inicial',
  integrations: 'Integracoes',
  finance: 'Configuracoes Financeiras',
  first_project: 'Primeiro Projeto',
  first_land: 'Primeiro Lote',
  first_contract: 'Primeiro Contrato'
}

// Ordem das categorias
const categoryOrder = ['setup', 'integrations', 'finance', 'first_project', 'first_land', 'first_contract']

export default defineComponent({
  name: 'OnboardingWidget',
  components: {
    OnboardingGroup,
    OnboardingCelebration
  },
  setup() {
    const vm = getCurrentInstance()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const root = vm?.proxy as any

    // Estado local
    const showCelebration = ref(false)
    const expandedPanel = ref<number | undefined>(0)
    const hasFullscreenDialog = ref(false)
    let fullscreenObserver: MutationObserver | null = null

    // Estado da sidebar (sincronizado com Vuex)
    const isExpanded = computed({
      get: () => root?.$store?.state?.onboardingModule?.isExpanded ?? true,
      set: (value: boolean) => {
        root?.$store?.commit('onboardingModule/SET_EXPANDED', value)
        // Salvar no localStorage
        localStorage.setItem('onboarding_expanded', value.toString())
      }
    })

    const steps = computed(() => {
      return root?.$store?.state?.onboardingModule?.steps || []
    })

    const progress = computed(() => {
      return root?.$store?.state?.onboardingModule?.progress || []
    })

    // Porcentagem total de conclusao
    const totalProgress = computed(() => {
      if (steps.value.length === 0) return 0
      return Math.round((progress.value.length / steps.value.length) * 100)
    })

    // Set de stepKeys completados para lookup rapido
    const completedStepKeys = computed(() => {
      return new Set(progress.value.map((p: { stepKey: string }) => p.stepKey))
    })

    // Steps agrupados por categoria
    const stepsByCategory = computed(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: Record<string, any[]> = {}
      steps.value.forEach((step: { category: string }) => {
        if (!result[step.category]) {
          result[step.category] = []
        }
        result[step.category].push(step)
      })
      return result
    })

    // Progresso por categoria com detalhes
    const progressByCategory = computed(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: Record<string, any> = {}
      Object.keys(stepsByCategory.value).forEach(category => {
        const categorySteps = stepsByCategory.value[category]
        const completedCount = categorySteps.filter((step: { key: string }) => completedStepKeys.value.has(step.key)).length

        result[category] = {
          steps: categorySteps.map((step: { key: string }) => ({
            ...step,
            isCompleted: completedStepKeys.value.has(step.key)
          })),
          completed: completedCount,
          total: categorySteps.length,
          percent: categorySteps.length > 0 ? Math.round((completedCount / categorySteps.length) * 100) : 0
        }
      })
      return result
    })

    const hasCompletedAll = computed(() => {
      return root?.$store?.state?.onboardingModule?.hasCompletedAll ?? false
    })

    // Logica de visibilidade do widget (definido apos as dependencias)
    const shouldShowWidget = computed(() => {
      // Se ja completou tudo, nao mostra
      if (hasCompletedAll.value) return false

      // Se nao tem steps carregados ainda, nao mostra
      if (steps.value.length === 0) return false

      return true
    })

    const totalStepsCount = computed(() => steps.value.length)
    const completedStepsCount = computed(() => progress.value.length)

    // Ordenar categorias na ordem correta
    const orderedCategories = computed(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: Record<string, any> = {}
      categoryOrder.forEach(category => {
        if (progressByCategory.value[category]) {
          result[category] = progressByCategory.value[category]
        }
      })
      return result
    })

    // Encontra o indice do primeiro grupo incompleto
    const findFirstIncompleteGroupIndex = (): number => {
      const categories = Object.keys(orderedCategories.value)
      for (let i = 0; i < categories.length; i++) {
        const category = categories[i]
        const categoryData = orderedCategories.value[category]
        if (categoryData.completed < categoryData.total) {
          return i
        }
      }
      return 0 // Retorna 0 se todos estiverem completos
    }

    // Metodos
    const toggleExpanded = () => {
      isExpanded.value = !isExpanded.value
    }

    const getCategoryTitle = (category: string): string => {
      return categoryTitles[category] || category
    }

    // Verificar primeira visita e tamanho da tela
    const checkFirstVisit = () => {
      // Em telas lg+, sempre inicia expandido
      if (root?.$vuetify?.breakpoint?.lgAndUp) {
        isExpanded.value = true
        return
      }

      // Em telas menores, usa estado salvo ou primeira visita
      const hasVisited = localStorage.getItem('onboarding_visited')
      if (!hasVisited) {
        // Primeira visita - garantir que sidebar esta aberta
        isExpanded.value = true
        localStorage.setItem('onboarding_visited', 'true')
      } else {
        // Recuperar estado salvo
        const savedExpanded = localStorage.getItem('onboarding_expanded')
        if (savedExpanded !== null) {
          isExpanded.value = savedExpanded === 'true'
        }
      }
    }

    // Handler para evento de onboarding completo
    const handleOnboardingComplete = () => {
      // Verificar se ja viu a celebracao
      const celebrationSeen = localStorage.getItem('onboarding_celebration_seen')
      if (!celebrationSeen) {
        showCelebration.value = true
      }
    }

    // Carregar dados do onboarding
    const fetchData = async () => {
      try {
        await root?.$store?.dispatch('onboardingModule/fetchOnboardingData')
      } catch (error) {
        console.error('Erro ao carregar dados de onboarding:', error)
      }
    }

    // Verificar se existe dialog fullscreen no DOM
    const checkFullscreenDialog = () => {
      hasFullscreenDialog.value = !!document.querySelector('.v-dialog--fullscreen')
    }

    // Configurar observer para detectar dialogs fullscreen
    const setupFullscreenObserver = () => {
      fullscreenObserver = new MutationObserver(() => {
        checkFullscreenDialog()
      })

      fullscreenObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      })
    }

    // Lifecycle
    onMounted(() => {
      checkFirstVisit()

      // Escutar evento de onboarding completo
      Vue.bus.$on('onboarding:completed', handleOnboardingComplete)

      // Fallback: se userId ja existe no mount, buscar dados
      if (steps.value.length === 0) {
        fetchData()
      }

      // Configurar observer para dialogs fullscreen
      setupFullscreenObserver()
      checkFullscreenDialog()

      // Expandir o primeiro grupo incompleto ao carregar
      if (Object.keys(orderedCategories.value).length > 0) {
        expandedPanel.value = findFirstIncompleteGroupIndex()
      }
    })

    onUnmounted(() => {
      Vue.bus.$off('onboarding:completed', handleOnboardingComplete)

      // Limpar observer
      if (fullscreenObserver) {
        fullscreenObserver.disconnect()
        fullscreenObserver = null
      }
    })

    // Watch para detectar quando completa 100% (via totalProgress)
    watch(totalProgress, (newVal, oldVal) => {
      if (newVal === 100 && oldVal !== undefined && oldVal < 100) {
        triggerCelebration()
      }
    })

    // Watch mais direto em hasCompletedAll (backup)
    watch(hasCompletedAll, (newVal, oldVal) => {
      if (newVal && !oldVal) {
        triggerCelebration()
      }
    })

    // Funcao centralizada para disparar celebracao
    const triggerCelebration = () => {
      const celebrationSeen = localStorage.getItem('onboarding_celebration_seen')
      if (!celebrationSeen && !showCelebration.value) {
        // Usar nextTick + pequeno delay para garantir que o Vue processou tudo
        nextTick(() => {
          setTimeout(() => {
            showCelebration.value = true
          }, 100)
        })
      }
    }

    // Watch para expandir proximo grupo quando o atual for completado
    watch(progress, () => {
      // Verifica se o grupo atualmente expandido esta completo
      const categories = Object.keys(orderedCategories.value)
      const currentIndex = expandedPanel.value ?? 0
      const currentCategory = categories[currentIndex]

      if (currentCategory) {
        const currentCategoryData = orderedCategories.value[currentCategory]
        // Se o grupo atual esta 100% completo, expande o proximo incompleto
        if (currentCategoryData && currentCategoryData.completed === currentCategoryData.total) {
          const nextIncompleteIndex = findFirstIncompleteGroupIndex()
          // So muda se houver um grupo incompleto diferente do atual
          if (nextIncompleteIndex !== currentIndex) {
            expandedPanel.value = nextIncompleteIndex
          }
        }
      }
    }, { deep: true })

    // Watch para expandir primeiro grupo incompleto quando dados sao carregados
    watch(steps, (newSteps) => {
      if (newSteps.length > 0 && Object.keys(orderedCategories.value).length > 0) {
        expandedPanel.value = findFirstIncompleteGroupIndex()
      }
    }, { once: true })

    return {
      isExpanded,
      showCelebration,
      expandedPanel,
      shouldShowWidget,
      hasFullscreenDialog,
      totalProgress,
      progressByCategory,
      orderedCategories,
      totalStepsCount,
      completedStepsCount,
      toggleExpanded,
      getCategoryTitle
    }
  }
})
</script>

<style lang="scss" scoped>
.onboarding-widget {
  z-index: 9998;
}

// FAB Styles
.onboarding-fab {
  z-index: 9998;
  width: 56px !important;
  height: 56px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3) !important;
  }
}

.progress-circle {
  position: relative;
}

.progress-text {
  font-size: 11px;
  font-weight: 700;
}

// FAB animation
.fab-fade-enter-active,
.fab-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fab-fade-enter,
.fab-fade-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

// Sidebar Styles
.onboarding-sidebar {
  z-index: 10001 !important;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15) !important;
}

.sidebar-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.header-title {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

.progress-section {
  background-color: #fafafa;
}

.groups-container {
  max-height: calc(100vh - 280px);
  overflow-y: auto;

  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }
}

.sidebar-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

// Expansion panels customization
::v-deep .v-expansion-panels {
  .v-expansion-panel {
    margin-bottom: 8px;
    border-radius: 8px !important;
    border: 1px solid rgba(0, 0, 0, 0.08);

    &::before {
      box-shadow: none !important;
    }

    &--active {
      border-color: var(--v-primary-base);
    }
  }

  .v-expansion-panel-header {
    min-height: 52px;
    padding: 12px 16px;

    &--active {
      min-height: 52px;
    }
  }
}
</style>
